import { push } from "connected-react-router";
import { useState, useMemo, useCallback } from "react";
import Modal from "react-modal";
import { useDispatch, useSelector } from "react-redux";
import {
  APPLICATION_MODE_NEW,
  APPLICATION_TYPE_MORTGAGE_BROKERAGE_LICENCE,
  APPLICATION_TYPE_MORTGAGE_PRINCIPAL_BROKER_LICENCE,
  ROUTE_PATH,
  VIEW_MODE_EDIT_PREVIEW_BEFORE_SUBMIT,
  DOCUMENT_TYPE_CRIMINAL_RECORD,
  DOCUMENT_CONTENT_TYPE_PDF,
  DOCUMENT_TYPE_EDUCATION_CERTIFICATE,
  DOCUMENT_TYPE_CONTINUING_EDUCATION_CERTIFICATE,
  LICENCE_SUB_TYPE_MORTGAGE_BROKER_RENEWAL_APPLICATION,
  LICENCE_SUB_TYPE_PRINCIPAL_BROKER_RENEWAL_APPLICATION,
  LICENCE_SUB_TYPE_ASSOCIATE_MORTGAGE_BROKER_RENEWAL_APPLICATION,
  MORTGAGE_PROPERTY_KEYS,
  APPLICATION_TYPE_MORTGAGE_ASSOCIATE_BROKER_LICENCE
} from "../../../app/constants";
import {
  Header,
  Button,
  Paragraph,
  ErrorMark,
  MainSeparator
} from "../../../components/RegularComponents";
import {
  DocumentViewerFileNameLink,
  DownloadFileNameLink,
} from "../../../components/DocumentViewer/DocumentFileComponent";
import { convertFileDataForUrl } from "../../../app/documentUploadUtils";
import DocumentViewer from "../../../components/DocumentViewer/DocumentViewer";
import ReviewForm from "../../../components/ReviewForm/ReviewForm";
import TopNavigationBar from "../../TopNavigationBar";
import { setCurrentAgentMortgageProperty } from "../../../redux/slice/additionalApplicationsSlice";
import { saveBusinessLicenceApplicationData } from "../../../redux/slice/businessLicenceApplicationSlice";
import { ERROR_STRING } from "../../../app/errors";
import {
  currentAgentApplicationSelector,
  currentAgentCachePropertiesSelector 
} from "../../../redux/selector/collectorApplicationSelector";
import { applicationTypeFromLicenceSubType, isCepRipExpiryYearSameAsRenewalYearForAgentApp, isRenewalTypeApplication } from "../../../redux/slice/applicationsUtils";
import { fetchDocumentData } from "../../../redux/slice/supportingDocumentsSlice";

import {decideLegalAndOperatingNames, fullName, humanReadableDate } from "../../../app/utils";

import { MraRenewalBrokerContactInformation } from './MraRenewalBrokerContactInformation';
import { MraRenewalBrokerCriminalRecordCheck } from './MraRenewalBrokerCriminalRecordCheck';
import { MraRenewalBrokerEducationCertificate } from './MraRenewalBrokerEducationCertificate';
import { MraRenewalBrokerContinuingEducationCertificate } from './MraRenewalBrokerContinuingEducationCertificate';
import { MraRenewalPrincipalBrokerActRegulation } from './MraRenewalPrincipalBrokerActRegulation';
import { ConfirmMortgageBrokerQualification } from "./ConfirmMortgageBrokerQualification";
import { agencyApplicationSelector } from "../../../redux/selector/agencyApplicationSelector";
import { INTERNAL_ID_PREFIX } from "../../../redux/slice/primaryAgencyApplicationSlice";

const MORTGAGE_APPLICATION_PROPERTY = "userApplicationReviewComplete";

export const MraRenewalBrokerApplicationReview = (props) => {
  const dispatch = useDispatch();
  const [applicationSaveError, setApplicationSaveError] = useState(false);
  const [openDocumentViewer, setOpenDocumentViewer] = useState(false);
  const [documentViewerDocument, setDocumentViewerDocument] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  const agentApplication = useSelector(currentAgentApplicationSelector);
  const { agencyApplication } = useSelector(agencyApplicationSelector);

  const cacheProperties = useSelector(currentAgentCachePropertiesSelector);

  const licenceTypeInfo  = useSelector(state => {
      return state.primaryAgencyApplication?.licenceTypeDetails?.businessLicenceStatusList?.[0]
  });
  
  const licencingSubType = agentApplication?.licencingSubType ?? LICENCE_SUB_TYPE_MORTGAGE_BROKER_RENEWAL_APPLICATION;
  const isPrincipalBroker = ( licencingSubType === LICENCE_SUB_TYPE_PRINCIPAL_BROKER_RENEWAL_APPLICATION )
  const isAssociateBroker = ( licencingSubType === LICENCE_SUB_TYPE_ASSOCIATE_MORTGAGE_BROKER_RENEWAL_APPLICATION )
  const isMortgageBroker = ( licencingSubType === LICENCE_SUB_TYPE_MORTGAGE_BROKER_RENEWAL_APPLICATION)

  let regulationActAccepted = false;
  if(isMortgageBroker || isAssociateBroker || isPrincipalBroker) {
    regulationActAccepted = !!cacheProperties[MORTGAGE_PROPERTY_KEYS.BROKER_DUTIES_CONFIRMATION];
  }

  const mraApplicationState = agentApplication.businessLicenceData ?? {};
  const documents = agentApplication?.documents;
  const {
    personData,
    mraRenewalCrcNoChangeIds,
    mraBrokerRegulatoryInfoDate,
    mraBrokerMpcCompletionDate
  } = mraApplicationState;
  
  const applicationType = applicationTypeFromLicenceSubType(licencingSubType);
  const applicationMode = APPLICATION_MODE_NEW;
  // const personData = agentApplication?.businessLicenceData?.personData ?? {};

  const uploadCriminalRecordCheck = useMemo(() => {
    const noChangeIds = mraRenewalCrcNoChangeIds.split(",")
    const entityId = `${INTERNAL_ID_PREFIX}-broker`

    if(noChangeIds.findIndex(id => id === entityId) !== -1) {
      return []
    }

    return documents.filter(
      (document) =>
        document.entityId === entityId && 
        document.documentType === DOCUMENT_TYPE_CRIMINAL_RECORD &&
        document.changeType !== "DELETE"
    )
  }
  , [documents, mraRenewalCrcNoChangeIds])

  const educationCertificates = useMemo(() => documents?.filter(
    (document) =>
      document.documentType === DOCUMENT_TYPE_EDUCATION_CERTIFICATE &&
      document.changeType !== "DELETE" 
  ), [documents])

  const educationContinuingCertificates = useMemo(() => {
    const docs = agentApplication?.documents
    const lastIssuedDate = agentApplication?.issuedDate

    return docs?.filter(
      (document) => (document.documentType === DOCUMENT_TYPE_CONTINUING_EDUCATION_CERTIFICATE &&
              document.changeType !== "DELETE" && 
              (!document.createdDate || document.createdDate >= lastIssuedDate))
          )
    }, [agentApplication])

  const [editComponent, setEditComponent] = useState(null);
  
  const [showEditDialog, setShowEditDialog] = useState(false);

  const COMPONENT_KEYS = {
    APPLICANT_CONTACT: "applicantContact",
    CRIMINAL_RECORDS_CHECK : "criminalRecords",
    EDUCATION_CERTIFICATE : "educationCertificate",
    PRINCIPAL_BROKER_CONFIRMATION : "principalBrokerConfirmation",
    COMPLIANCE_OFFICER: "complianceOfficer",
    EDUCATION_CONTINUING_CERTIFICATE: "educationContinuingCertificate",
  };


  // const SubSeparator = () => {
  //   return (
  //     <hr
  //       className="bottomMarginNormal"
  //       style={{
  //         borderColor: "#0058a466",
  //         borderWidth: "3px",
  //         width: "60%",
  //         marginLeft: "0px",
  //       }}
  //     />
  //   );
  // };

  const documentReviewComponent = useCallback((file) => {
    const fileComponents = [];

    if (file.documentContentType === DOCUMENT_CONTENT_TYPE_PDF) {
      fileComponents.push(
        <div style={{ maxWidth: "40rem", overflow: "hidden" }}>
          <DocumentViewerFileNameLink
            id={"document-view-link-" + file.fileName}
            key={"document-view-link-" + file.fileName}
            file={file}
            onClick={() => {
              if (file.data) {
                let fileDataUrl = convertFileDataForUrl(
                  file.data,
                  file.documentContentType
                );
                setDocumentViewerDocument(fileDataUrl);
                setSelectedFile(file);
                setOpenDocumentViewer(true);
              } else {
                // download file data and then show it
                dispatch(fetchDocumentData(file.guid))
                  .then(downloadFile => {
                      if(downloadFile) { 
                          let fileDataUrl = convertFileDataForUrl(downloadFile.data, downloadFile.documentContentType)
                          setDocumentViewerDocument(fileDataUrl)
                          setSelectedFile(downloadFile)
                          setOpenDocumentViewer(true)
                      }
                  })
                  .catch((error) => {
                      console.error(error)
                  })
              }
            }}
          />
        </div>
      );
    } else {
      fileComponents.push(
        <DownloadFileNameLink
          id={"download-link-" + file.fileName}
          componentKey={"download-link-" + file.fileName}
          file={file}
        />
      );
    }

    return fileComponents;
  }, [
    dispatch,
    setDocumentViewerDocument,
    setSelectedFile,
    setOpenDocumentViewer
  ])

  const titleForComponent = (componentKey) => {
    switch (componentKey) {
      case COMPONENT_KEYS.APPLICANT_CONTACT:
        return "Applicant Contact";
      case COMPONENT_KEYS.CRIMINAL_RECORDS_CHECK:
        return "Criminal Record Check";
      case COMPONENT_KEYS.EDUCATION_CERTIFICATE : 
        return "Education Certificate";
      case COMPONENT_KEYS.EDUCATION_CONTINUING_CERTIFICATE : 
        return "Education Continuing Certificate";
      case COMPONENT_KEYS.PRINCIPAL_BROKER_CONFIRMATION : 
        return "MRA Regulations Confirmation";
      case COMPONENT_KEYS.COMPLIANCE_OFFICER:
        if(isMortgageBroker) {
          return "Mortgage Broker Qualifications"
        } else if(isAssociateBroker) {
          return "Associate Mortgage Broker Qualifications"
        } else if(isPrincipalBroker) {
          return "Principal Broker Qualifications"
        }
        return "Compliance Officer Qualifications";
        
      default:
        return componentKey;
    }
  };

  /*=== click handlers == */
  const handleEditContactInfo = () => {
    setShowEditDialog(true);
    setEditComponent(COMPONENT_KEYS.APPLICANT_CONTACT);
  };

  const handleEditCOQualifications = () => {
    setShowEditDialog(true);
    setEditComponent(COMPONENT_KEYS.COMPLIANCE_OFFICER);
  }

  const handleEditCriminalRecordsCheck = () => {
    setShowEditDialog(true);
    setEditComponent(COMPONENT_KEYS.CRIMINAL_RECORDS_CHECK);
  };

  const handleEditEducationCertificate = () => {
    setShowEditDialog(true);
    setEditComponent(COMPONENT_KEYS.EDUCATION_CERTIFICATE);
  };

  const handleEditEducationContinuingCertificate = () => {
    setShowEditDialog(true);
    setEditComponent(COMPONENT_KEYS.EDUCATION_CONTINUING_CERTIFICATE);
  };

  const handleEditPrincipalBrokerConfirmation = () => {
    setShowEditDialog(true);
    setEditComponent(COMPONENT_KEYS.PRINCIPAL_BROKER_CONFIRMATION);
  };

  // const handleEditBrokerEducation = () => {
  //   setShowEditDialog(true);
  //   setEditComponent(COMPONENT_KEYS.BROKER_EDUCATION);
  // };

  // const handleEditAssociateBrokerApplicant = () => {
  //   setShowEditDialog(true);
  //   setEditComponent(COMPONENT_KEYS.ASSOCIATE_APPLICANT_NAME);
  // };

  // const handleEditAssociateCriminalRecord = () => {
  //   setShowEditDialog(true);
  //   setEditComponent(COMPONENT_KEYS.ASSOCIATE_CRIMINAL_RECORD);
  // };

  // const handleEditAssociateWorkHistory = () => {
  //   setShowEditDialog(true);
  //   setEditComponent(COMPONENT_KEYS.ASSOCIATE_WORK_HISTORY);
  // }

  // this component is needed only for the new application types.
  const getEducationCertificateDetails = () => {
    if(isRenewalTypeApplication(agentApplication) || 
      agentApplication.licencingSubType === LICENCE_SUB_TYPE_ASSOCIATE_MORTGAGE_BROKER_RENEWAL_APPLICATION) {
      return null;
    }
    return (
      <>
        <MainSeparator />
        <div className="row">
          <div className="col-lg-8">
            <h3>Education</h3>
            {educationCertificates?.length > 0 ? 
              <ReviewForm 
                fullSize
                data={{
                  "Latest education certificate" : documentReviewComponent(educationCertificates.sort((f1,f2) => f1.createdDate < f2.createdDate)[0])
                }} 
              />
              : (
                <span>No Education certificate uploaded.</span>
              )
          }
          </div>
        </div>

        <div className="row">
            <div className="col-lg-3">
                <Button
                  style={{ marginTop: "10px", textDecoration: "underline"}}
                  buttonType={"regular_link"}
                  text={"Update education certificate"}
                  onClick={handleEditEducationCertificate}
                />
            </div>
        </div>
      </>
    )
  }

  // this component is needed only for the renewal type applications
  const getEducationContinuingCertificateDetails = () => {
    if (!isRenewalTypeApplication(agentApplication)) {
      return null;
    }

    //To avoid updated cep and regulatory dates as expiry dates are yet to get calulated
    let canShowCEPExpiryDate = agentApplication?.cepRipExpiryDate ? true : false;
    if (isCepRipExpiryYearSameAsRenewalYearForAgentApp(agentApplication, licenceTypeInfo)) {
      // For updated documents and date, expiry date is yet to get calculated from backend. So avoid showing.
      // Backend calculates the expiry date on App submit.
      canShowCEPExpiryDate = false;
    }

    let applicationType = APPLICATION_TYPE_MORTGAGE_BROKERAGE_LICENCE;

    if (agentApplication) {
      const { licencingSubType } = agentApplication;
      applicationType = applicationTypeFromLicenceSubType(licencingSubType);
    }

    const isAMBApplication = applicationType === APPLICATION_TYPE_MORTGAGE_ASSOCIATE_BROKER_LICENCE;

    let reviewData = {};

    if (!isAMBApplication) {
      if(mraBrokerRegulatoryInfoDate) {
         reviewData["Regulatory Information Program completion date"] = mraBrokerRegulatoryInfoDate
        ? humanReadableDate(mraBrokerRegulatoryInfoDate)
        : "---";
      }
     
      if (agentApplication?.cepRipExpiryDate) {
        reviewData["Regulatory Information Program valid until date"] =
          agentApplication.cepRipExpiryDate
            ? humanReadableDate(agentApplication.cepRipExpiryDate)
            : "---";
      }
    }

    if(mraBrokerMpcCompletionDate) {
      reviewData["MPC Continuing Education Program completion date"] = mraBrokerMpcCompletionDate
      ? humanReadableDate(mraBrokerMpcCompletionDate)
      : "---";
    }
    

    if (agentApplication?.cepRipExpiryDate) {
      reviewData["MPC Continuing Education Program valid until date"] =
        agentApplication?.cepRipExpiryDate
          ? humanReadableDate(agentApplication.cepRipExpiryDate)
          : "---";
    }

    //Show the updated document.
    if (!canShowCEPExpiryDate) {
      reviewData["Latest education continuing certificate"] =
        educationContinuingCertificates?.length > 0
          ? documentReviewComponent(
              educationContinuingCertificates.sort((f1, f2) => f2.createdDate - f1.createdDate)[0]
            )
          : "No Education continuing certificate uploaded.";
    }

    return (
      <>
        <MainSeparator />
        <div className="row">
          <div className="col-lg-8">
            <h3 className="bottomMarginMedium">Education Continuation</h3>
            <ReviewForm fullSize data={reviewData} />
          </div>
        </div>
        {canShowCEPExpiryDate ? null : (
          <div className="row">
            <div className="col-lg-3">
              <Button
                style={{ marginTop: "10px", textDecoration: "underline" }}
                buttonType={"regular_link"}
                text={"Update education continuing information"}
                onClick={handleEditEducationContinuingCertificate}
              />
            </div>
          </div>
        )}
      </>
    );
  };
  

  const getRegulationActDeclarationDetails = () => {
    let declarationtext = [
      `Have you read the Mortgage Regulation Act and regulations? <b> ${regulationActAccepted ? "Yes" : "No"} </b>`,
      `Do you agree to be bound by the Mortgage Regulation Act's provisions? <b> ${regulationActAccepted ? "Yes" : "No"} </b>`
    ]

    if(isMortgageBroker || isAssociateBroker) {
      const brokerName = fullName(agentApplication?.businessLicenceData?.personData)
      const pbName = fullName(agencyApplication?.businessLicenceData?.personData)

      declarationtext = [
        `I, ${pbName} confirm that ${brokerName} has read and is aware of the contents and requirements or the Mortgage Regulation Act (MRA) and MRA regulations and agrees to be bound by its provisions? <b> ${regulationActAccepted ? "YES" : "NO"}</b>`,
        `I, ${pbName} confirm that ${brokerName} has been in compliance with the Act and its Regulations? <b> ${regulationActAccepted ? "YES" : "NO"}</b>`,
      ]
    }

    return (
      <div className="row">
      <div className="col-lg-8">
        <h3>MRA Regulations Confirmation</h3>
        <Paragraph 
        text={declarationtext}
        />
      </div>
    </div>
    )
  }

  return (
    <>
      <div className={"container"}>
        <TopNavigationBar {...props} applicationType={applicationType} applicationMode={applicationMode} />
        <div className="row">
          <div className="col-xs-12 col-lg-9">
              <Header
                id="broker-application-review-header"
                header={isPrincipalBroker ? 
                  "Principal Broker's Mortgage Licence Renewal" : isAssociateBroker ? 
                  "Associate Mortgage Broker Licence Renewal" : "Mortgage Broker Licence Renewal"}
              />
              <Paragraph
                id="broker-application-review-text"
                text={[
                  "Please review and ensure the information you have provided is correct.",
                ]}
              />
          </div>
        </div>

        <MainSeparator />
        {/* Personal Information */}
        <div className="row">
          <div className="col-lg-8">
            <h3>Personal information</h3>
            <ReviewForm
              fullSize
              data={{
                "First name": personData?.firstName ?? "",
                "Last name": personData?.lastName ?? "",
                "Home Address":
                      personData?.addressData?.address1 ?? "",
                "City/Town": personData?.addressData?.cityName ?? "",
                Province: personData?.addressData?.stateName ?? "",
                "Postal code":
                  personData?.addressData?.postalCode ?? "",
                "Phone number": personData?.phoneNumber ?? "",
                "Alternate phone number":
                  personData?.alternatePhoneNumber ?? "",
                  "Email" : personData?.email ?? ""
              }}
            />
          </div>
        </div>
        <div className="row">
            <div className="col-lg-3">
                <Button
                  style={{ marginTop: "10px", textDecoration: "underline"}}
                  buttonType={"regular_link"}
                  text={"Update contact information"}
                  onClick={handleEditContactInfo}
                />
            </div>
        </div>

        <ComplianceOfficerQualifications
          handleEditCOQualifications={handleEditCOQualifications} 
        />
        
        <MainSeparator />
        <div className="row">
          <div className="col-lg-8">
            <h3>Criminal Record Check</h3>
            {uploadCriminalRecordCheck?.length > 0 ? 
            <ReviewForm 
              fullSize
              data={{
                "Latest criminal record check file" : documentReviewComponent(uploadCriminalRecordCheck.sort((f1,f2) => f1.createdDate < f2.createdDate)[0])
              }}
            />: (
              <span><b>No, </b> I confirm there have not been any changes to the previous criminal record check I submitted to the Registrar.</span>
            )}
          </div>
        </div>

        <div className="row">
            <div className="col-lg-3">
                <Button
                  style={{ marginTop: "10px", textDecoration: "underline"}}
                  buttonType={"regular_link"}
                  text={"Update criminal record check"}
                  onClick={handleEditCriminalRecordsCheck}
                />
            </div>
        </div>

        {getEducationCertificateDetails()}
        {getEducationContinuingCertificateDetails()}

        <MainSeparator />

        {getRegulationActDeclarationDetails()}

        <div className="row">
            <div className="col-lg-3">
                <Button
                  style={{ marginTop: "10px", textDecoration: "underline"}}
                  buttonType={"regular_link"}
                  text={"Update confirmation"}
                  onClick={handleEditPrincipalBrokerConfirmation}
                />
            </div>
        </div>
        
        <MainSeparator />

        <div className="row">
          <div
            className={"col-lg-4" + applicationSaveError ? " has-error" : ""}
          >
            <Button
              id="complete-mortgage-education-continue-button"
              style={{ margin: "0 10px 10px 0" }}
              buttonType={"regular_primary"}
              text={"Save and next"}
              onClick={() => {
                
                // setting this flag will make the application elibigle for the payment.
                dispatch(
                  setCurrentAgentMortgageProperty({
                    key: MORTGAGE_APPLICATION_PROPERTY,
                    value: true,
                  })
                );

                const promiseArray = []
                const p1 = dispatch(
                  saveBusinessLicenceApplicationData(
                    APPLICATION_TYPE_MORTGAGE_PRINCIPAL_BROKER_LICENCE,
                    applicationMode
                  )
                );
                promiseArray.push(p1)

                // only in case the review is for principal broker
                if(isPrincipalBroker) { 
                  // save the main agency application, just in case
                  // the details like principal broker name, etc. is
                  // modified
                  const p2 = dispatch(
                    saveBusinessLicenceApplicationData(
                      APPLICATION_TYPE_MORTGAGE_BROKERAGE_LICENCE,
                      applicationMode,
                      false,
                      "UPDATE"
                    )
                  );
                  promiseArray.push(p2)
                }
                
                Promise.all(promiseArray)
                  .then((values) => {
                    // On to the next item
                    dispatch(push({
                        pathname : `${ROUTE_PATH.MRA_APP}${ROUTE_PATH.MRA_RENEWAL_CONFIRM_BEFORE_PAYMENT}`,
                        state: {applicationId : agentApplication.id} 
                    }))
                  })
                  .catch((err) => {
                    setApplicationSaveError(true);
                  });
              }}
            />
            {applicationSaveError && ErrorMark()}
            {applicationSaveError && (
              <div className="ns-error-message help-block topMarginNormal">
                <span className="ns-visually-hidden">Error:</span>
                {ERROR_STRING.APPLICATION_SAVE_ERROR}
              </div>
            )}
          </div>
        </div>
      </div>

      <Modal
        appElement={document.getElementById("root")}
        isOpen={showEditDialog}
        contentLabel="editDetailsDialog"
        onRequestClose={() => {
          setShowEditDialog(false);
        }}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        className="modalDialogContent editDetailsModalDialog"
        closeTimeoutMS={150}
      >
        <div className="row">
          <div className="col-11">
            <div className="row">
              <div className="col-12">
                <h2>{titleForComponent(editComponent)}</h2>
                <MainSeparator />
              </div>
            </div>
            <div className="row">
              <div className="col-12">
                {editComponent === COMPONENT_KEYS.APPLICANT_CONTACT && (
                  <MraRenewalBrokerContactInformation
                    isModal
                    viewMode={VIEW_MODE_EDIT_PREVIEW_BEFORE_SUBMIT}
                    onEditSave={() => setShowEditDialog(false)}
                    {...props}
                  />
                )}
              </div>
            </div>

            <div className="row">
              <div className="col-12">
                {editComponent === COMPONENT_KEYS.COMPLIANCE_OFFICER && (
                  <ConfirmMortgageBrokerQualification
                    isModal
                    viewMode={VIEW_MODE_EDIT_PREVIEW_BEFORE_SUBMIT}
                    onEditSave={() => setShowEditDialog(false)}
                    {...props}
                  />
                )}
              </div>
            </div>

            <div className="row">
              <div className="col-12">
                {editComponent === COMPONENT_KEYS.CRIMINAL_RECORDS_CHECK && (
                  <MraRenewalBrokerCriminalRecordCheck
                    isModal
                    viewMode={VIEW_MODE_EDIT_PREVIEW_BEFORE_SUBMIT}
                    onEditSave={() => setShowEditDialog(false)}
                    {...props}
                  />
                )}
              </div>
            </div>
            <div className="row">
              <div className="col-12">
                {editComponent === COMPONENT_KEYS.EDUCATION_CERTIFICATE && (
                  <MraRenewalBrokerEducationCertificate
                    isModal
                    viewMode={VIEW_MODE_EDIT_PREVIEW_BEFORE_SUBMIT}
                    onEditSave={() => setShowEditDialog(false)}
                    {...props}
                  />
                )}
              </div>
            </div>
            <div className="row">
              <div className="col-12">
                {editComponent === COMPONENT_KEYS.EDUCATION_CONTINUING_CERTIFICATE && (
                  <MraRenewalBrokerContinuingEducationCertificate
                    isModal
                    viewMode={VIEW_MODE_EDIT_PREVIEW_BEFORE_SUBMIT}
                    onEditSave={() => setShowEditDialog(false)}
                    {...props}
                  />
                )}
              </div>
            </div>
            <div className="row">
              <div className="col-12">
                {editComponent === COMPONENT_KEYS.PRINCIPAL_BROKER_CONFIRMATION && (
                  <MraRenewalPrincipalBrokerActRegulation
                    isModal
                    viewMode={VIEW_MODE_EDIT_PREVIEW_BEFORE_SUBMIT}
                    onEditSave={() => setShowEditDialog(false)}
                    {...props}
                  />
                )}
              </div>
            </div>
          </div>
          <div className="col-1">
            <div
              className="closeButton pull-right"
              onClick={() => {
                setShowEditDialog(false);
              }}
            >
              <i className="fa fa-2x fa-times"></i>
            </div>
          </div>
        </div>
      </Modal>

      <DocumentViewer
        fileData={documentViewerDocument}
        fileName={selectedFile?.filename}
        isOpen={openDocumentViewer}
        handleClose={() => setOpenDocumentViewer(false)}
      />
    </>
  );
};


const ComplianceOfficerQualifications = (props) => {
	const currentAgentApp = useSelector(currentAgentApplicationSelector);
	const cacheProperties = useSelector(currentAgentCachePropertiesSelector);
	if (!cacheProperties) {
		return null;
	}

	if (
		!(
			currentAgentApp.licencingSubType ===
				LICENCE_SUB_TYPE_ASSOCIATE_MORTGAGE_BROKER_RENEWAL_APPLICATION ||
			currentAgentApp.licencingSubType ===
				LICENCE_SUB_TYPE_MORTGAGE_BROKER_RENEWAL_APPLICATION
		)
	) {
		return null;
	}

	const companyRjscDetails = currentAgentApp?.businessLicenceData?.companyRjscDetails;
	const { legalName, operatingName } = decideLegalAndOperatingNames(companyRjscDetails);

	const accepted = cacheProperties[MORTGAGE_PROPERTY_KEYS.IS_CO_QUALIFICATIONS_ACCEPTED]
		? "Yes"
		: "No";

	let userType, roleTypeText;
	if (
		currentAgentApp.licencingSubType ===
		LICENCE_SUB_TYPE_ASSOCIATE_MORTGAGE_BROKER_RENEWAL_APPLICATION
	) {
		userType = "Associate Mortgage Broker";
		roleTypeText = "an associate mortgage broker";
	} else {
		userType = "Mortgage Broker";
		roleTypeText = "a mortgage broker";
	}

	let orgAuthText =
		operatingName && legalName
			? `${operatingName}(${legalName})`
			: operatingName
			? operatingName
			: legalName
			? legalName
			: "";

	return (
		<>
			<MainSeparator />
			<div className="row">
				<div className="col-lg-10" style={{ marginLeft: "15px" }}>
					<Paragraph
						style={{ paddingTop: "10px" }}
						id="compliance-officer-review-text"
						text={[`<h3>${userType} Qualifications<h3>`]}
					/>
					<div className={"col-8"}>
						<ol className="principalBrokerQualificationReqList">
							<li>
								Is the {userType} <b>19 years of age or older</b>? {"  "}
								{accepted}
							</li>
							<li>
								Is the {userType} a <b>resident of Canada</b>?{"  "}
								{accepted}
							</li>
							<li>
								Is the {userType} <b>authorized</b> to carry out the{" "}
								<b>duties and responsibilities</b> of {roleTypeText} on behalf of{" "}
								<b>{orgAuthText}</b>? {"  "}
								{accepted}
							</li>
						</ol>
					</div>
				</div>
				<div className="col-lg-3">
					<Button
						style={{ marginTop: "10px", textDecoration: "underline" }}
						buttonType={"regular_link"}
						text={"Update qualifications status"}
						onClick={props.handleEditCOQualifications}
					/>
				</div>
			</div>
		</>
	);
};